import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Card, CardHeader } from "../../components/ui/Card";
import { Modal } from "../../components/ui/Modal";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import type { PersonalItem, PersonalItemCategory } from "../../types";

/**
 * Katalogverwaltung für das Modul "Eigener Lagerbestand" - Gegenstände und
 * Kategorien anlegen/bearbeiten/löschen. Nur sichtbar mit
 * "personal_inventory.manage_catalog" (Standard: President/VizePresident).
 */
export function CatalogManagementTab() {
  const [categories, setCategories] = useState<PersonalItemCategory[]>([]);
  const [items, setItems] = useState<PersonalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryModal, setCategoryModal] = useState<PersonalItemCategory | null | undefined>(undefined);
  const [itemModal, setItemModal] = useState<PersonalItem | null | undefined>(undefined);

  function load() {
    setLoading(true);
    Promise.all([
      api.get<PersonalItemCategory[]>("/api/personal-inventory/categories"),
      api.get<PersonalItem[]>("/api/personal-inventory/items"),
    ]).then(([c, i]) => { setCategories(c); setItems(i); }).finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function deleteCategory(cat: PersonalItemCategory) {
    if (!confirm(`Kategorie "${cat.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/personal-inventory/categories/${cat.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  async function deleteItem(item: PersonalItem) {
    if (!confirm(`Gegenstand "${item.name}" wirklich löschen? Dies entfernt ihn auch aus allen Beständen.`)) return;
    try {
      await api.delete(`/api/personal-inventory/items/${item.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Kategorien"
          subtitle="Gruppierung der RP-Gegenstände"
          action={<button className="btn-secondary" onClick={() => setCategoryModal(null)}>+ Neue Kategorie</button>}
        />
        {categories.length === 0 ? <EmptyState message="Keine Kategorien vorhanden." /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-surface-raised border border-surface-border">
                <div>
                  <p className="text-sm font-medium text-gray-200">{cat.name}</p>
                  <p className="text-xs text-gray-500">{cat.item_count ?? 0} Gegenstände</p>
                </div>
                <div className="flex gap-2">
                  <button className="text-accent text-xs hover:underline" onClick={() => setCategoryModal(cat)}>Bearbeiten</button>
                  <button className="text-danger text-xs hover:underline" onClick={() => deleteCategory(cat)}>Löschen</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card padded={false}>
        <div className="px-5 pt-5">
          <CardHeader
            title="Gegenstände"
            subtitle="Katalog der frei definierbaren RP-Gegenstände"
            action={<button className="btn-secondary" onClick={() => setItemModal(null)}>+ Neuer Gegenstand</button>}
          />
        </div>
        {items.length === 0 ? <EmptyState message="Keine Gegenstände vorhanden." /> : (
          <table className="table-base">
            <thead><tr><th>Name</th><th>Kategorie</th><th>Einheit</th><th>Status</th><th /></tr></thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="font-medium text-gray-100">{item.name}</td>
                  <td className="text-gray-400">{item.category_name ?? "—"}</td>
                  <td className="text-gray-400">{item.unit}</td>
                  <td className="text-gray-400">{item.is_active ? "Aktiv" : "Inaktiv"}</td>
                  <td className="text-right whitespace-nowrap">
                    <button className="text-accent text-xs hover:underline mr-3" onClick={() => setItemModal(item)}>Bearbeiten</button>
                    <button className="text-danger text-xs hover:underline" onClick={() => deleteItem(item)}>Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {categoryModal !== undefined && (
        <CategoryFormModal category={categoryModal} onClose={() => setCategoryModal(undefined)} onSaved={() => { setCategoryModal(undefined); load(); }} />
      )}
      {itemModal !== undefined && (
        <ItemFormModal item={itemModal} categories={categories} onClose={() => setItemModal(undefined)} onSaved={() => { setItemModal(undefined); load(); }} />
      )}
    </div>
  );
}

function CategoryFormModal({ category, onClose, onSaved }: { category: PersonalItemCategory | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(category?.name ?? "");
  const [description, setDescription] = useState(category?.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (category) await api.put(`/api/personal-inventory/categories/${category.id}`, { name, description });
      else await api.post("/api/personal-inventory/categories", { name, description });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={category ? "Kategorie bearbeiten" : "Neue Kategorie"}
      onClose={onClose}
      footer={<>
        <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
        <button className="btn-primary" form="pi-category-form" disabled={submitting}>Speichern</button>
      </>}
    >
      <form id="pi-category-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div><label className="label">Name *</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} required /></div>
        <div><label className="label">Beschreibung</label><textarea className="input" rows={3} value={description ?? ""} onChange={(e) => setDescription(e.target.value)} /></div>
      </form>
    </Modal>
  );
}

function ItemFormModal({ item, categories, onClose, onSaved }: { item: PersonalItem | null; categories: PersonalItemCategory[]; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(item?.name ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [categoryId, setCategoryId] = useState(item?.category_id ?? "");
  const [unit, setUnit] = useState(item?.unit ?? "Stk");
  const [isActive, setIsActive] = useState(item?.is_active !== 0);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = { name, description, categoryId: categoryId ? Number(categoryId) : null, unit, isActive };
      if (item) await api.put(`/api/personal-inventory/items/${item.id}`, payload);
      else await api.post("/api/personal-inventory/items", payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={item ? "Gegenstand bearbeiten" : "Neuer Gegenstand"}
      onClose={onClose}
      footer={<>
        <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
        <button className="btn-primary" form="pi-item-form" disabled={submitting}>Speichern</button>
      </>}
    >
      <form id="pi-item-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div><label className="label">Name *</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} required /></div>
        <div><label className="label">Beschreibung</label><textarea className="input" rows={2} value={description ?? ""} onChange={(e) => setDescription(e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Kategorie</label>
            <select className="input" value={categoryId ?? ""} onChange={(e) => setCategoryId(e.target.value as any)}>
              <option value="">— keine —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label className="label">Einheit</label><input className="input" value={unit} onChange={(e) => setUnit(e.target.value)} /></div>
        </div>
        {item && (
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Aktiv (im Katalog auswählbar)
          </label>
        )}
      </form>
    </Modal>
  );
}
